import React, { useRef, useEffect } from 'react'
import { CopyFieldTemplate } from 'components/common'
import LazyGrid from 'components/common/CommonGrids/LazyGrid'
import { useSelector, useDispatch } from 'react-redux'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Checkbox } from 'primereact/checkbox'
import { Badge } from 'primereact/badge'
import ContributorNameBodyTemplate from 'components/common/CommonGrids/Columns/ContributorNameBodyTemplate'
import { fileDownload } from 'api/FileDownload/fileDownload'
import {
    selectAudioVisualEpisodeListHasChanges,
    selectEpisodeListRowInEditCount,
    selectAudiovisualFilter,
    selectAudiovisualSort,
    selectAudioVisualResultOriginal,
    selectEpisodeDataUpdateKey,
    selectEpisodeListResultUpdateStatus,
    selectEpisodeListUpdateRequest,
    selectShowEpisodeDataUpdate,
    selectMergeChangeEpisodeListToDetail,
    selectEpisodeListCurrentPage,
    selectEpisodeListFirst,
    selectEpisodeListRows,
    selectEpisodeListTotalPages,
    selectEpisodeListFetchStatus,
    selectMergedEpisodeListData,
    selectProgrammeUrl
} from 'components/Body/data-entry/AudioVisual/store/selectors'
import {
    setIsNewEpisodeAction,
    setAudiovisualFilterAction,
    setAudiovisualSortAction,
    episodeListstartEditing,
    episodeListstopEditing,
    setProgrammeDraftResultsEpisodesAction,
    setProgrammeEpisodesFetchResultsAction,
    clearProgrammeDraftResultsEpisodesAction,
    clearProgrammeDraftResultsEpisodesListAction,
    setEpisodeGuidAction,
    setEpisodeFetchResultsAction,
    setEpisodeListUpdateStatusAction,
    setProgrammeOriginalAggregateVersionAction,
    setShowEpisodeDataUpdateAction,
    setEpisodeListCurrentPageAction,
    setEpisodeListFirstAction,
    setEpisodeListRowsAction,
    setEpisodeListTotalPagesAction,
} from 'components/Body/data-entry/AudioVisual/store/actions'
import { push } from 'redux-first-history'
import DataListUpdate from 'api/DataListUpdate/AudioVisual'
import { Toast } from 'primereact/toast'
import { ExportFormat, EnglishLanguageId } from 'modules/Constants'
import { getErrorMessage } from 'modules/ErrorMessages'
import { getOrangeBadgeClass } from 'components/common/CommonGrids/utils/badgeUtils'

// ReSharper disable once InconsistentNaming
export default function EpisodesList(props) {
    const toast = useRef(null)
    const isFilm = props.isFilm

    const programmeDataOriginal = useSelector(selectAudioVisualResultOriginal)
    // For display - merged data (shows draft changes)
    const episodeListPageData = useSelector(selectMergedEpisodeListData)
    const isEditMode = useSelector(selectEpisodeListRowInEditCount) > 0
    const programmeUrl = useSelector(selectProgrammeUrl)
    const filter = useSelector(selectAudiovisualFilter)
    const sort = useSelector(selectAudiovisualSort)

    // Episodes from selector are array format for LazyGrid
    const episodeData = episodeListPageData?.episodes ?? []
    // Episodes in store are object keyed by episodeId - use directly for O(1) lookup
    const episodeDataOriginal = programmeDataOriginal?.episodes ?? {}
    const originalEpisodesCount = episodeListPageData?.episodesCount ?? 0

    const draftAvailable = useSelector(selectAudioVisualEpisodeListHasChanges)
    const updatedEpisode = useSelector(selectMergeChangeEpisodeListToDetail)

    const dispatch = useDispatch()
    const setAcceptedEpisode = (episode) => dispatch(setProgrammeDraftResultsEpisodesAction(episode))
    const clearUnchangedEpisode = () => dispatch(clearProgrammeDraftResultsEpisodesAction())
    const setNewEpisode = (newEpisode) => dispatch(setEpisodeFetchResultsAction(newEpisode))
    const setIsNewEpisode = (isNewEpisode) => dispatch(setIsNewEpisodeAction(isNewEpisode))
    const setEpisodeGuid = (guid) => dispatch(setEpisodeGuidAction(guid))
    const showDataUpdate = useSelector(selectShowEpisodeDataUpdate)
    const dataUpdateKey = useSelector(selectEpisodeDataUpdateKey)
    const setShowDataUpdate = (show) => dispatch(setShowEpisodeDataUpdateAction(show))
    const clearProgrammeEpisodeListDraftResults = () => dispatch(clearProgrammeDraftResultsEpisodesListAction())
    const updateStatus = useSelector(selectEpisodeListResultUpdateStatus)
    const excelDownloadName = 'ProgrammeEpisodes.xlsx'

    useEffect(() => {
        if (updateStatus?.updateEpisodeListStatus === 'complete') {
            dispatch(setEpisodeListUpdateStatusAction({ updateEpisodeListStatus: 'idle' }))
            setShowDataUpdate(false)
            clearProgrammeEpisodeListDraftResults()
            // Store episodes as object keyed by episodeId (required by mergeChanges)
            const episodeDataAsObject = episodeData.reduce((acc, episode) => {
                acc[episode.episodeId] = { ...episode, id: episode.episodeId }
                return acc
            }, {})
            dispatch(setProgrammeEpisodesFetchResultsAction(episodeDataAsObject))
            dispatch(setEpisodeFetchResultsAction(updatedEpisode))
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Changes Saved Successfully', life: 3000 })
        } else if (updateStatus?.updateEpisodeListStatus === 'error') {
            const error = updateStatus?.error
            dispatch(setEpisodeListUpdateStatusAction({ updateEpisodeListStatus: 'idle' }))
            setShowDataUpdate(false)
            
            // Use specific error message from API if available, otherwise use generic message
            const errorMessage = error?.ErrorMessage || error?.Details || getErrorMessage(error?.StatusCode || updateStatus?.StatusCode)
            toast.current.show({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 })
        }
    }, [updateStatus])

    const navigateTo = route => {
        dispatch(push(route))
    }

    const handleUpdateComplete = () => {
        setShowDataUpdate(false)
    }

    const isRowDataChanged = (rowData) => {
        const originalData = episodeDataOriginal[rowData.episodeId]
        return originalData && JSON.stringify(originalData) !== JSON.stringify(rowData)
    }

    const displayActions = function (rowData, options) {
        const handleEpisodeNavigation = () => {
            navigateTo(`/AudioVisual/${programmeDataOriginal.id}/${rowData.episodeId}`)
        }
        const isChanged = isRowDataChanged(rowData)

        if (!rowData.contributions?.length) {
            return (
                <div id={props.id + '_avList_EpisodeListingGrid_actions'} className="d-flex justify-content-start align-items-center">
                    <Button icon="fa-light fa-arrow-up-right" rounded onClick={handleEpisodeNavigation} disabled={isChanged || isEditMode} />
                    <Button className="ml-2" icon="pi pi-copy" rounded disabled />
                </div>
            )
        }

        const { expandedRows, onRowToggle, dataKey } = options
        const isExpanded = expandedRows?.[rowData[dataKey]]

        return (
            <div id={props.id + '_avList_EpisodeListingGrid_actions'} className="d-flex justify-content-start align-items-center">
                <Button icon="fa-light fa-arrow-up-right" rounded onClick={handleEpisodeNavigation} disabled={isChanged || isEditMode} />
                <Button className="ml-2" icon="pi pi-copy" rounded disabled />
                <Button
                    icon="pi pi-users"
                    rounded
                    className={`ml-2 ${isExpanded ? 'p-button-info' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation()
                        const newExpandedRows = isExpanded
                            ? Object.fromEntries(Object.entries(expandedRows || {}).filter(([k]) => k !== String(rowData[dataKey])))
                            : { ...(expandedRows || {}), [rowData[dataKey]]: true }
                        onRowToggle({ data: Object.keys(newExpandedRows).length ? newExpandedRows : null })
                    }}
                />
            </div>
        )
    }

    const handleAddEpisode = () => {
        const defaultEpisode = {
            id: crypto.randomUUID(),
            durationMinutes: 0,
            idaCode: "",
            yearOfProduction: new Date().getFullYear(),
            number: 0,
            series: 0,
            sapCode: "",
            comments: "",
            programmeSapCode: "",
            titles: [{
                title: "Episode " + (originalEpisodesCount + 1),
                languageId: EnglishLanguageId,
                titleTypeId: 1
            }],
            transmissions: [],
            createdOn: new Date().toISOString(),
            updatedOn: new Date().toISOString()
        }
        setEpisodeGuid(defaultEpisode.id)
        setNewEpisode(defaultEpisode)
        setIsNewEpisode(true)
        navigateTo(`/AudioVisual/${programmeDataOriginal.id}/${defaultEpisode.id}`)
    }

    const handleExport = (format) => {
        const queryParams = new URLSearchParams({
            ...(filter && { filter: filter.toString() }),
            ...(sort && { sortBy: sort.toString() })
        }).toString()

        if (format === ExportFormat.Excel) {
            const endpoint = `${programmeUrl}/${programmeDataOriginal.id}/EpisodesExcelExport?${queryParams}`
            fileDownload(endpoint, excelDownloadName)
        } else if (format === ExportFormat.Csv) {
            const endpoint = `${programmeUrl}/${programmeDataOriginal.id}/EpisodesCsvExport?${queryParams}`
            fileDownload(endpoint, excelDownloadName.replace('.xlsx', '.csv'))
        }
    }

    const header = (
        <div className="row d-flex">
            <div className="col mt-1">
                <Button id={props.id + '_container_episodeList_dataTable_add'} className="mr-3 justify-content-start" label="Add Episode" icon="fa-light fa-plus" disabled={isFilm || draftAvailable || isEditMode} onClick={handleAddEpisode} />
            </div>
            <div className="col-auto mt-1">
                <Button id={props.id + '_container_episodeList_dataTable_export_csv'} className="mr-3 justify-content-end" label="CSV Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Csv)} disabled={draftAvailable} />
                <Button id={props.id + '_container_episodeList_dataTable_export_excel'} className="mr-3 justify-content-end" label="Excel Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Excel)} disabled={draftAvailable} />
            </div>
        </div>
    )

    const validateData = (rowData) => {
        const errors = {}
        if (!rowData.episodeTitle || rowData.episodeTitle.trim() === '') errors.episodeTitle = 'Episode Title is Mandatory'
        if (rowData.episodeNumber === null || rowData.episodeNumber < 0) errors.episodeNumber = 'Episode Number is Mandatory'
        if (rowData.episodeSeries === null || rowData.episodeSeries < 0) errors.episodeSeries = 'Series Number is Mandatory'
        if (rowData.episodeDuration === null || rowData.episodeDuration < 0) errors.episodeDuration = 'Duration is Mandatory'
        if (rowData.episodeYear === null || rowData.episodeYear <= 0) errors.episodeYear = 'Year Produced is Mandatory'
        if (rowData.episodeIda && String(rowData.episodeIda).length !== 12) {
            errors.episodeIda = 'IDA must be 12 characters long'
        }
        if (rowData.episodeIda && isNaN(rowData.episodeIda)) {
            errors.episodeIda = 'Episode IDA should be numeric'
        }
        if (rowData.episodeIda && props.episodeListDraft && Object.keys(props.episodeListDraft).length > 0) {
            const isIdaInDraftList = Object.values(props.episodeListDraft).some(episode => episode.episodeIda === rowData.episodeIda && episode.episodeId !== rowData.episodeId)
            if (isIdaInDraftList) {
                errors.episodeIda = 'IDA already exists'
            }
        }
        return errors
    }

    const handleRowEditSave = (rowData) => {
        const originalData = episodeDataOriginal[rowData.episodeId]
        if (originalData && JSON.stringify(originalData) === JSON.stringify(rowData)) {
            clearUnchangedEpisode()
        } else {
            setAcceptedEpisode({ [rowData.episodeId]: rowData })
        }
    }

    const displayEpisodeCode = function (rowData) {
        return CopyFieldTemplate(rowData, 'episodeCode', props.id + '_avList_EpisodeListingGrid_copy')
    }

    const displayEpisodeTitle = function (rowData) {
        return CopyFieldTemplate(rowData, 'episodeTitle', props.id + '_avList_EpisodeListingGrid_copy')
    }

    const displayIdaCode = function (rowData) {
        return CopyFieldTemplate(rowData, 'episodeIda', props.id + '_avList_EpisodeListingGrid_copy')
    }

    const contributionsExpansionTemplate = (data) => {
        const formatMinutes = (rowData) => {
            return rowData.minutes ? parseFloat(rowData.minutes.toFixed(2)) : ''
        }

        const formatBoolean = (rowData, field) => {
            return <Checkbox checked={rowData[field] || false} disabled={true} />
        }

        const formatMembershipTypes = (rowData) => {
            const memberships = rowData.memberships
            if (!memberships || !Array.isArray(memberships) || memberships.length === 0) {
                return null
            }
            return (
                <div className="d-flex flex-wrap align-items-center gap-1">
                    {memberships.map((membership, index) => (
                        <Badge
                            key={index}
                            value={membership.membershipTypeName}
                            className={`custom-badge-base ${getOrangeBadgeClass(membership.membershipTypeId)}`}
                        />
                    ))}
                </div>
            )
        }

        const formatAuthorKey = (rowData) => CopyFieldTemplate(rowData, 'authorKey')
        const formatIpiNumber = (rowData) => CopyFieldTemplate(rowData, 'ipiNumber')
        const formatContributorName = (rowData) => (
            <ContributorNameBodyTemplate rowData={rowData} field="name" />
        )

        return (
            <div id={props.id + '_episodeList_commonGrid_nestedTable'} className="p-3">
                <DataTable value={data.contributions} scrollable scrollHeight="430px" emptyMessage="No Contributors">
                    <Column field="authorKey" header="Author Key" body={formatAuthorKey}></Column>
                    <Column field="name" header="Contributor Name" body={formatContributorName}></Column>
                    <Column field="contributionType" header="Contribution"></Column>
                    <Column field="percentage" header="Percent (%)"></Column>
                    <Column field="minutes" header="Minutes Worked" body={formatMinutes}></Column>
                    <Column field="ipiNumber" header="IPI No" body={formatIpiNumber} className="ipi-column"></Column>
                    <Column field="isPublicDomain" header="Public Domain" body={(rowData) => formatBoolean(rowData, 'isPublicDomain')}></Column>
                    <Column field="isExcluded" header="Excluded" body={(rowData) => formatBoolean(rowData, 'isExcluded')}></Column>
                    <Column field="memberships" header="Membership Types" body={formatMembershipTypes}></Column>
                </DataTable>
            </div>
        )
    }

    const episodeColumnDefinitions = [
        { columnType: 'text', title: 'Ep.Code', mapsToField: 'episodeCode', template: displayEpisodeCode, filter: true, filterMatchMode: FilterMatchMode.STARTS_WITH, sortable: true, editable: false },
        { columnType: 'text', title: 'Ep.Title', mapsToField: 'episodeTitle', template: displayEpisodeTitle, filterMatchMode: FilterMatchMode.STARTS_WITH, filter: true, sortable: true, editable: true },
        { columnType: 'numeric', title: 'Ep.No', mapsToField: 'episodeNumber', filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true, editable: true },
        { columnType: 'numeric', title: 'Series No.', mapsToField: 'episodeSeries', filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true, editable: true },
        { columnType: 'numeric', title: 'Duration', mapsToField: 'episodeDuration', filter: false, filterMatchMode: FilterMatchMode.EQUALS, sortable: false, editable: true },
        { columnType: 'year', title: 'Year Produced', mapsToField: 'episodeYear', filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true, editable: true },
        { columnType: 'idaCode', title: 'IDA No', mapsToField: 'episodeIda', template: displayIdaCode, filter: true, filterMatchMode: FilterMatchMode.EQUALS, sortable: true, editable: true },
        { columnType: 'actions', template: displayActions }
    ]

    return (
        <div id={props.id + '_episodeList'}>
            <Toast ref={toast} />
            {showDataUpdate && (
                <DataListUpdate
                    id={props.id + '_DataListUpdate_audioVisual_Episode'}
                    key={dataUpdateKey}
                    setStatusAction={setEpisodeListUpdateStatusAction}
                    requestSelector={selectEpisodeListUpdateRequest}
                    setAggregateAction={setProgrammeOriginalAggregateVersionAction}
                    onComplete={handleUpdateComplete}
                />
            )}
            <LazyGrid
                id={props.id + '_episodeList_commonGrid'}
                dataKey="episodeId"
                data={episodeData}
                originalData={episodeDataOriginal}
                totalCount={originalEpisodesCount}
                emptyMessage="Programme has no Episodes"
                columnDefinitions={episodeColumnDefinitions}
                selectTotalPages={selectEpisodeListTotalPages}
                selectFirst={selectEpisodeListFirst}
                selectRows={selectEpisodeListRows}
                fetchStatusSelector={selectEpisodeListFetchStatus}
                setTotalPagesAction={setEpisodeListTotalPagesAction}
                setCurrentPageAction={setEpisodeListCurrentPageAction}
                setFirstAction={setEpisodeListFirstAction}
                setRowsAction={setEpisodeListRowsAction}
                setFilterAction={setAudiovisualFilterAction}
                setSortAction={setAudiovisualSortAction}
                selectionMode="multiple"
                selectionStyle="checkbox"
                scrollHeight="650px"
                allowRowEdits={true}
                header={header}
                validateData={validateData}
                onSave={handleRowEditSave}
                startEditing={episodeListstartEditing}
                stopEditing={episodeListstopEditing}
                rowExpansionTemplate={contributionsExpansionTemplate}
                collapsedRowIcon="pi pi-users"
                expandedRowIcon={(row, expandedRows) =>
                    <i className={`pi pi-users ${expandedRows && expandedRows[row.episodeId] ? 'custom-expanded' : ''}`} />
                }
            />
        </div>
    )
}
